// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import SearchInput, { SearchInputStyles } from "@insite/content-library/Widgets/Header/SearchInput";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import HeaderClickable from "@synd/App/Components/Header/HeaderClickable";
import { CustomAppState } from "@synd/App/Store/Reducers";
import Close from "@synd/XDL/Icons/Close";
import Search from "@synd/XDL/Icons/HeaderSearch";
import * as cssLinter from "css";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { css } from "styled-components";

const enum fields {
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.customCSS]: string;
    };
}

export interface HeaderSearchInputStyles {
    // XNG-Changes
    searchWrapper?: InjectableCss;
    searchInputStyles?: SearchInputStyles;
}

export const searchInputStyles: HeaderSearchInputStyles = {};

const defaultCustomCss = `.search-input{
}

.popover{
}

.autocomplete-wrapper{
}

.autocomplete-column-wrapper{
}

.autocomplete-categories-header{
}

.autocomplete-categories-item-text{
}

.autocomplete-categories-item-link{
}

.autocomplete-categories-link{
}

.autocomplete-brands-header{
}

.autocomplete-brands-item-text{
}

.autocomplete-brands-item-link{
}

.autocomplete-brands-link{
}

.autocomplete-content-header{
}

.autocomplete-content-link{
}

.autocomplete-products-header{
}

.autocomplete-products-container{
}

.autocomplete-products-image-grid-item{
}

.autocomplete-products-image{
}

.autocomplete-products-info-grid-item{
}

.autocomplete-products-link{
}

.autocomplete-products-erp-number{
}`;

export const HeaderSearchInput: React.FC<OwnProps & { isAnimated?: boolean }> = ({
    id,
    fields,
    isAnimated = false,
}) => {
    // <XNG-Changes>
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const { isTabletScreen, navDrawerIsOpen } = useAppSelector(state => {
        return {
            isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
            navDrawerIsOpen: state.components.addressDrawer.navDrawerIsOpen,
        };
    });

    const dispatch = useDispatch();

    useEffect(() => {
        if (navDrawerIsOpen) {
            setIsSearchOpen(false);
        }
    }, [navDrawerIsOpen]);

    useEffect(() => {
        if (navDrawerIsOpen && isSearchOpen) {
            dispatch(setNavDrawerIsOpen({ navDrawerIsOpen: false }) as any);
        }
    }, [isSearchOpen]);
    // </XNG-Changes>

    const styles = useMergeStyles("headerSearchInput", searchInputStyles);

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    return (
        <>
            {isTabletScreen && (
                <HeaderClickable
                    data-test-selector="search"
                    aria-label={translate("Search")}
                    icon={{
                        src: isSearchOpen ? Close : Search,
                        color: isSearchOpen ? "common.ui11" : "common.ui9",
                        size: 19,
                    }}
                    isOpen={isSearchOpen}
                    onClick={() => setIsSearchOpen(!isSearchOpen)}
                />
            )}
            <StyledWrapper
                {...customCssWrapper}
                // XNG-Changes
                {...styles.searchWrapper}
                {...{ iconMode: isTabletScreen, isOpen: isSearchOpen }}
            >
                <SearchInput
                    id={id}
                    isAnimated={isAnimated}
                    extendedStyles={styles.searchInputStyles}
                    // XNG-Changes
                    {...{ onSearchCompleted: () => setIsSearchOpen(false) }}
                />
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: HeaderSearchInput,
    definition: {
        group: "Header",
        displayName: "Search Input",
        allowedContexts: ["Header"],
        fieldDefinitions: [
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
